import i18n from '@/i18n'

/** HTTP statuses this util branches on. */
const HttpStatus = {
  BAD_REQUEST: 400,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
}

/** Codes this util produces itself, when the response carries none. */
export const ClientErrorCode = {
  UNKNOWN: 'UNKNOWN',
  SERVER_ERROR: 'SERVER_ERROR'
}

/** Keys under `api.errors.*` used when no backend code maps to a message. */
const FallbackMessageKey = {
  UNKNOWN: 'unknown',
  SERVER_ERROR: 'server_error'
}

export default {
    /** True when the API answered 404: the requested record does not exist (or is not public). */
    isNotFound(error){
      return error?.response?.status === HttpStatus.NOT_FOUND
    },

    /** processError may not be a best name, this should be more like getErrorMessages */
    processError(error, formContext = null){
      console.log('Process error: ', error)
      return this.getProcessedError(error, formContext).messages;
    },

    /** This is our main method to process error and generate proper translation */
    getProcessedError(error, formContext = null){
      if (error && error.response){
        if (error.response.status === HttpStatus.BAD_REQUEST && error.response.data){
          let errorCode = error.response.data.code
          if (errorCode){
            // Handle VALIDATION_FAILED specifically
            if (errorCode === 'VALIDATION_FAILED' && error.response.data.details) {
              let errorMessages = []
              for (const [fieldName, fieldErrorCode] of Object.entries(error.response.data.details)) {
                // Create field-specific error message with form context
                let fieldErrorMessage = this._getFieldErrorMessage(fieldName, fieldErrorCode, formContext)
                if (fieldErrorMessage) {
                  errorMessages.push(fieldErrorMessage)
                } else {
                  // Fallback to generic field error, and to the generic message when even the raw
                  // code has no translation - pushing null here leaves the caller with a blank toast.
                  errorMessages.push(this._getTranslation(fieldErrorCode)
                    || this._getTranslation(FallbackMessageKey.UNKNOWN))
                }
              }
              if (errorMessages.length > 0) {
                return {code: errorCode, messages: errorMessages, fields: error.response.data.details}
              }
            }
            
            // Merge details and detailsMap for translation params
            const details = error.response.data.details || {}
            const detailsMap = error.response.data.detailsMap || {}
            
            const params = { ...details, ...detailsMap }
            let errorMessage = this._getTranslation(errorCode, params)
            if (errorMessage){
              return { code: errorCode, messages: [errorMessage], fields: params } 
            }
            console.error('Missing error message for code: ', errorCode)
          }
          let details = error.response.data.details
          if (details){
            let errorMessages = []
            for (const [key, value] of Object.entries(details)) {
              let errorMessage = this._getTranslation(value)
              console.log(`${key}: ${value}`, errorMessage);
              if (!errorMessage){
                errorMessage = this._getTranslation(FallbackMessageKey.UNKNOWN)
              }
              errorMessages.push(errorMessage)
            }

            if (errorMessages && errorMessages.length > 0){
              return { code: errorCode, messages: errorMessages }
            }
          }
        } else if (error.response.status === HttpStatus.NOT_FOUND){
          return HttpStatus.NOT_FOUND
        } else if (error.response.status >= HttpStatus.INTERNAL_SERVER_ERROR){
          // The request was fine and we broke. "Try again in a moment" is wrong advice here - the
          // same call fails the same way - so say whose fault it is and where to get help.
          return { code: ClientErrorCode.SERVER_ERROR, messages: [this._getTranslation(FallbackMessageKey.SERVER_ERROR)] }
        }
      }
      console.error('General error message!')
      return { code: ClientErrorCode.UNKNOWN, messages: [this._getTranslation(FallbackMessageKey.UNKNOWN)] }
    },

    processClientError(error){
      let clientError = this.processError(error)
      if (clientError === 404){
        //Redirect to 404 page
        window.location.href = window.location.pathname + "/not-found"
        return null
      }
      return clientError
    },

    _getTranslation(errorConstant, params){
      if (!errorConstant){
        return null
      }

      // Try lowercase first (for error codes like 'validation_failed')
      let translation = 'api.errors.' + errorConstant.toLowerCase()
      let errorMessage = i18n.t(translation, params)
      if (errorMessage !== translation){
        return errorMessage
      }
      
      // Try original case (for error codes like 'SIGNUP_EMAIL_EXISTS')
      translation = 'api.errors.' + errorConstant
      errorMessage = i18n.t(translation, params)
      if (errorMessage !== translation){
        return errorMessage
      }
      
      return null
    },

    _getFieldErrorMessage(fieldName, fieldErrorCode, formContext = null) {
      if (!fieldName || !fieldErrorCode) {
        return null
      }

      let localizedFieldName = null

      // Try to get field name from form context first
      if (formContext && formContext.getFieldLabel) {
        localizedFieldName = formContext.getFieldLabel(fieldName)
      }

      // Fallback to centralized API field names
      if (!localizedFieldName) {
        localizedFieldName = i18n.t(`api.errors.fields.${fieldName}`)
        // If translation doesn't exist, fall back to field name
        if (localizedFieldName === `api.errors.fields.${fieldName}`) {
          localizedFieldName = fieldName
        }
      }

      if (!localizedFieldName) {
        return this._getTranslation(fieldErrorCode)
      }
      
      // Get localized error message with field parameter
      const errorMessage = this._getTranslation(fieldErrorCode, { field: localizedFieldName })
      
      return errorMessage
    },

    /**
     * Safely gets the first error message from processError result
     * @param {*} error - The error object
     * @param {*} formContext - Optional form context for field-specific error messages
     * @returns {string} - The first error message or a fallback message
     */
    getSingleErrorMessage(error, formContext = null) {
      console.log('Get single error message: ', error)
      const result = this.processError(error, formContext)
      console.log('Result: ', result)
      
      // If result is an array and has items, return the first one
      if (Array.isArray(result) && result.length > 0) {
        return result[0]
      }
      
      // If result is a string, return it
      if (typeof result === 'string') {
        return result
      }
      
      // Fallback to unknown error
      return this._getTranslation(FallbackMessageKey.UNKNOWN)
    }
}